/**
 * RequireAdmin — Route guard for /admin/* routes.
 *
 * Enforces two invariants:
 *   1. Must have a session (else → /admin/login?next=<current>)
 *   2. Practitioner-tier roles are HARD-BLOCKED from /admin/*
 *      → immediate redirect to /practitioner, no flash of admin content
 *   3. `superOnly` mode adds a further check: role must be super_admin
 *      (else → back to /admin hub)
 *
 * Backend endpoints are already gated with `require_super_admin` /
 * `require_admin`. This component is purely the client-side information-
 * disclosure fence.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { adminAuth } from "@/lib/api";

const PRACTITIONER_ROLES = new Set(["practitioner_lite", "practitioner_pro"]);

export default function RequireAdmin({ children, superOnly = false }) {
  const location = useLocation();
  const token = adminAuth.getToken();
  if (!token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace data-testid="require-admin-redirect" />;
  }
  const role = adminAuth.getRole?.() || "";
  // ── HARD ROLE ISOLATION — practitioners never see any /admin/* route ──
  if (PRACTITIONER_ROLES.has(role)) {
    return <Navigate to="/practitioner" replace data-testid="require-admin-practitioner-block" />;
  }
  if (superOnly && !adminAuth.isSuperAdmin()) {
    return <Navigate to="/admin" replace data-testid="require-admin-not-super" />;
  }
  return children;
}
